import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { GoogleSuppliedAddressPart } from '../../util/AddressUtil';

const DialogActionsGrey = styled(DialogActions)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
}));

const AddressOptionCard = styled(Card)(({ theme }) => ({
  borderWidth: 2,
  borderStyle: 'solid',
  borderColor: theme.palette.divider,
  '&.ss-address-option-selected': {
    borderColor: theme.palette.primary.main,
  },
}));

export type AddressConfirmationSelection = 'suggested' | 'typed';

interface AddressConfirmationDialogProps {
  open: boolean;
  /** The address exactly as the user typed it (not selected from the dropdown). */
  typedAddress: string;
  /**
   * Google's top geocoded match for `typedAddress`. Omit when Google
   * couldn't resolve the street at all — the dialog then just asks the
   * supplier to confirm their own entry, with no alternative to compare.
   */
  suggestedAddress?: string;
  /**
   * The address parts that would be filled in from Google if the user keeps
   * their own entry, with the exact values that would be saved. When
   * non-empty, a warning listing them is shown on the "Your entry" option.
   */
  googleSuppliedParts?: GoogleSuppliedAddressPart[];
  onClose: () => void;
  onConfirm: (selection: AddressConfirmationSelection) => void;
}

/** Joins labels into a readable list, e.g. "City, State and ZIP code". */
const formatPartsList = (parts: string[]): string =>
  parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : parts[0];

/** Warning naming the parts Google will supply, with their exact values. */
const GoogleSuppliedPartsWarning = ({ parts }: { parts: GoogleSuppliedAddressPart[] }) => (
  <Alert
    severity="warning"
    role="note"
    icon={false}
    sx={{ mt: 1, py: 0, px: 1, '& .MuiAlert-message': { py: 0.5, width: '100%' } }}
  >
    <Typography variant="caption" component="div">
      Your entry is missing {parts.length > 1 ? '' : 'a '}
      {formatPartsList(parts.map((part) => part.label))}. We&apos;ll add
      {parts.length > 1 ? ' these' : ' this'} from Google&apos;s match:
    </Typography>
    <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
      {parts.map((part) => (
        <Typography key={part.label} component="li" variant="caption">
          {part.label}: <strong>{part.value}</strong>
        </Typography>
      ))}
    </Box>
  </Alert>
);

/**
 * Modal shown when a manually typed address needs confirmation. Normally
 * compares the typed address with Google's top suggestion side-by-side and
 * lets the user pick one. When Google couldn't resolve the street at all,
 * there's nothing to compare against, so it instead asks the user to
 * confirm their own entry will be saved with approximate coordinates.
 */
const AddressConfirmationDialog = (props: AddressConfirmationDialogProps) => {
  const { open, typedAddress, suggestedAddress, googleSuppliedParts = [], onClose, onConfirm } = props;
  const hasSuggestion = Boolean(suggestedAddress && suggestedAddress.trim());
  const [selection, setSelection] = useState<AddressConfirmationSelection>('suggested');
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Reset the default selection each time the dialog is (re)opened for a new
  // comparison, rather than persisting a prior choice. With no suggestion to
  // compare against, the supplier's own entry is the only possible outcome.
  React.useEffect(() => {
    if (open) {
      setSelection(hasSuggestion ? 'suggested' : 'typed');
    }
  }, [open, hasSuggestion]);

  const options: { value: AddressConfirmationSelection; label: string; address: string }[] = [
    { value: 'suggested', label: 'Suggested by Google', address: suggestedAddress || '' },
    { value: 'typed', label: 'Your entry', address: typedAddress },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="address-confirmation-title"
      aria-describedby="address-confirmation-description"
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
    >
      <DialogTitle id="address-confirmation-title" variant="h6">
        Confirm your address
      </DialogTitle>
      <DialogContent id="address-confirmation-description">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {hasSuggestion
            ? "We couldn't confidently verify the address you typed. Please choose the address you'd like to use."
            : "We couldn't verify this address with Google. We'll save it as you typed it, with approximate coordinates from the ZIP code."}
        </Typography>
        {hasSuggestion ? (
          <RadioGroup
            aria-label="Address options"
            name="address-confirmation-selection"
            value={selection}
            onChange={(event) => setSelection(event.target.value as AddressConfirmationSelection)}
          >
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              {options.map((option) => (
                <AddressOptionCard
                  key={option.value}
                  variant="outlined"
                  className={selection === option.value ? 'ss-address-option-selected' : ''}
                  sx={{ flex: 1 }}
                >
                  <CardActionArea
                    onClick={() => setSelection(option.value)}
                    sx={{ p: 2, height: '100%', alignItems: 'flex-start' }}
                  >
                    <FormControlLabel
                      value={option.value}
                      control={<Radio />}
                      sx={{ alignItems: 'flex-start', width: '100%', m: 0 }}
                      label={
                        <Box sx={{ ml: 1 }}>
                          <Chip
                            label={option.label}
                            size="small"
                            color={option.value === 'suggested' ? 'primary' : 'default'}
                            sx={{ mb: 1 }}
                          />
                          <Typography variant="body2">{option.address}</Typography>
                          {option.value === 'typed' && googleSuppliedParts.length > 0 && (
                            <GoogleSuppliedPartsWarning parts={googleSuppliedParts} />
                          )}
                        </Box>
                      }
                    />
                  </CardActionArea>
                </AddressOptionCard>
              ))}
            </Box>
          </RadioGroup>
        ) : (
          <AddressOptionCard variant="outlined" className="ss-address-option-selected">
            <Box sx={{ p: 2 }}>
              <Chip label="Your entry" size="small" sx={{ mb: 1 }} />
              <Typography variant="body2">{typedAddress}</Typography>
              {googleSuppliedParts.length > 0 && (
                <GoogleSuppliedPartsWarning parts={googleSuppliedParts} />
              )}
            </Box>
          </AddressOptionCard>
        )}
      </DialogContent>
      <DialogActionsGrey>
        <Button color="primary" onClick={onClose}>
          Cancel
        </Button>
        <Button color="primary" variant="contained" onClick={() => onConfirm(selection)}>
          Use this address
        </Button>
      </DialogActionsGrey>
    </Dialog>
  );
};

export default AddressConfirmationDialog;
